import { Component, computed, inject, input, signal, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { groupBy, GroupDescriptor, GroupResult } from '@progress/kendo-data-query';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgreementsClient, AgreementUserCreateDto, AgreementUsersClient, AssignableUsersClient, CreatedResponseDto } from '@unity/clients';
import { PageContainerComponent } from '@unity/components/layouts/page-container/page-container.component';
import { DropdownUserAssignControlComponent } from '@unity/forms/dropdown-user-assign-control/dropdown-user-assign-control.component';
import { ActionCancelSharedComponent } from '@unity/shared/composed/action-cancel-shared/action-cancel-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { nonZeroFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

class AgreementUserCreateSchema implements FormGroupControlSchema<AgreementUserCreateDto> {
  public agreement_id = nonZeroFormControl();
  public agency_id = nonZeroFormControl();
  public user_id = nonZeroFormControl();
}

@Component({
  selector: 'unity-new-agreement-user-page',
  imports: [
    DropdownUserAssignControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
    ActionCancelSharedComponent,
  ],
  templateUrl: './new-agreement-user-page.component.html',
  styleUrl: './new-agreement-user-page.component.scss',
})
export class NewAgreementUserPageComponent extends AbstractNewComponent<
  AgreementUserCreateDto,
  AgreementUserCreateSchema,
  CreatedResponseDto
> {
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly agreementUsersClient = inject(AgreementUsersClient);
  private readonly assignableUsersClient = inject(AssignableUsersClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = signal('Assign User to Agreement');
  protected override createResourceFn$ = (form: FormGroup<AgreementUserCreateSchema>) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.agreementUsersClient.addOneToAgreement(agreementId, form.getRawValue());
  };
  protected override redirectUrlFn = (_: CreatedResponseDto): UnityRouteUrlTrees => ['/agreement', this.$agreementId(), 'user'];
  protected override newControlsFn() {
    return new AgreementUserCreateSchema();
  }

  private kendoGroupByKey<T extends object>(things: T[], key: keyof T) {
    return groupBy(things, [{ field: key } as GroupDescriptor]) as GroupResult[];
  }

  protected agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsMini(agreementId),
  });

  protected assignableUsersResource = rxResource({
    defaultValue: [],
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.assignableUsersClient.getForAgreement(agreementId),
  });

  protected readonly $selectableUsers: Signal<GroupResult[]> = computed(() => {
    const users = this.assignableUsersResource.value();
    const usersWithGroupingKey = users.map(user => ({ ...user, groupingKey: user.is_internal ? 'Internal' : 'Business Partner' }));
    return this.kendoGroupByKey(usersWithGroupingKey, 'groupingKey');
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const agreement = this.agreementResource.value();
    return `Agreement: ${agreement?.name}`;
  });

  protected override onSubmit(): void {
    const formGroup = this.$formGroup();
    const agreement = this.agreementResource.value();
    if (!agreement) {
      throw new Error('Unable to find an agreement to assign to this user.');
    }

    formGroup.controls.agreement_id.patchValue(agreement.id);
    formGroup.controls.agency_id.patchValue(agreement.agency_id);
    super.onSubmit();
  }

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
